<template>
  <div class="row h-100">
    <div class="col-12 h-100">
      <div class="poppins planned-content h-100">
        <div class="h-100 d-flex flex-column" v-if="loaded">
          <BlockHeader name="Details" class="mt-0" />
          <div class="row">
            <div class="col-lg-6 col-sm-8 col-8">
              <Select v-model="selectedYear" :onChange="init" :options="yearOptions" form-group-id="Year" placeholder="year" />
            </div>
            <div class="col-lg-6 col-sm-4 col-4 actions-wrapper mt-4 pt-1">
              <b-button variant="primary" size="lg" @click="exportCSV" class="export-btn">
                {{ 'export' | i18n }}
              </b-button>
              <div class="arrows-block">
                <b-button variant="primary" size="lg" @click="scrollShift(-600)">
                  <BIconArrowLeft />
                </b-button>
                <b-button variant="primary" size="lg" @click="scrollShift(600)">
                  <BIconArrowRight />
                </b-button>
              </div>
            </div>
          </div>
          <div class="table-wrapper w-100 overflow-auto" v-if="data.length">
            <table class="roboto-mono" ref="tableData">
              <thead>
                <tr>
                  <th
                    v-for="h in header"
                    :key="h.name"
                    :style="`background:${h.color};`"
                    :colspan="h.length || 1"
                    class="metrics-header center-header"
                  >
                    <span class="metrics-number" :style="h.length ? 'padding:3px' : ''">
                      {{ h.length ? format(metrics[h.name]) : '' }}
                    </span>
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="h in header"
                    :key="h.name"
                    :style="`background:${h.color};`"
                    :colspan="h.length || 1"
                    :class="`${h.length ? 'no-wrap-header' : ''} center-header`"
                  >
                    {{ h.name | i18n }}
                  </th>
                </tr>
                <tr>
                  <th v-for="field in fields" :key="field.name" :style="`background:${field.color};`" class="field-name-header">
                    <div class="rotate-header">
                      {{ field.empty ? '' : field.name | i18n }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in data" :key="line.date">
                  <td
                    v-for="field in fields"
                    :key="`${field.name}-${line.date}`"
                    class="roboto-mono"
                    :style="`background:${field.color};white-space: nowrap;`"
                  >
                    {{ field.name === 'Date' || !field.obj ? line[field.name] : format(line[field.obj][field.name]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="no-data">
            {{ 'no_table_data' | i18n }}
          </div>
        </div>
        <Loader v-else />
      </div>
    </div>
  </div>
</template>

<script>
import { BIconArrowRight, BIconArrowLeft } from 'bootstrap-vue';
import moment from 'moment';
import api from '../../../api';

import PageHeader from '../../common/page-header';
import Select from '../../common/select';
import Loader from '../../common/loader';
import BlockHeader from '../../common/block-header';

const currentYear = Number(moment().format('YYYY'));

export default {
  title: 'RMVA',
  components: { PageHeader, Loader, BlockHeader, Select, BIconArrowRight, BIconArrowLeft },
  data() {
    return {
      loaded: false,
      isSaving: false,
      selectedYear: currentYear,
      yearOptions: [],
      data: [],
      fields: [
        {
          name: 'KW',
          empty: true,
        },
        {
          name: 'Anl. Tag',
          empty: true,
        },
        {
          name: 'Date',
          empty: true,
        },
      ],
      header: [
        {
          name: 'KW',
        },
        {
          name: 'Anl. Tag',
        },
        {
          name: 'Date',
        },
        {
          name: 'Hausmüll',
          color: 'rgb(167,200,136)',
          length: 9,
          fields: [
            'Hausmüll Straße Ist',
            'Hausmüll Straße Ist kumuliert',
            'Hausmüll Schiene Ist',
            'Hausmüll Schiene Ist kumuliert',
            'Hausmüll gesamt Ist',
            'Hausmüll gesamt Ist kumuliert',
            'Hausmüll gesamt Plan',
            'Hausmüll gesamt Abweichung',
            'Hausmüll gesamt Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Sperrmüll zu AVGR',
          color: 'rgb(152,185,224)',
          length: 5,
          fields: [
            'Sperrmüll zur AVGR Ist',
            'Sperrmüll zur AVGR Ist kumuliert',
            'Sperrmüll zur AVGR Plan kumuliert',
            'Sperrmüll zur AVGR Abweichung kumuliert',
            'Sperrmüll zur AVGR Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Sperrmüllrückführung',
          color: 'rgb(248,213,104)',
          length: 5,
          fields: [
            'Sperrmüllrückführung Ist',
            'Sperrmüllrückführung Ist kumuliert',
            'Sperrmüllrückführung Plan kumuliert',
            'Sperrmüllrückführung Abweichung kumuliert',
            'Sperrmüllrückführung Ist Wochenmittelwert',
          ],
        },
        {
          name: 'AWB zur RMVA',
          color: 'rgb(247,225,210)',
          length: 7,
          fields: [
            'AWB zur RMVA Ist',
            'AWB zur RMVA Ist kumuliert',
            'AWB zur RMVA Plan kumuliert',
            'AWB zur RMVA Abweichung kumuliert',
            'AWB zur RMVA Ist Wochenmittelwert',
            'AWB zur RMVA Ist Gleitender Mittelwert',
            'AWB zur RMVA Ist Containeranteil Stadt',
          ],
        },
        {
          name: 'AWB gesamt',
          color: 'rgb(234,234,234)',
          length: 6,
          fields: [
            'AWB gesamt Ist',
            'AWB gesamt Ist kumuliert',
            'AWB gesamt Plan kumuliert',
            'AWB gesamt Abweichung kumuliert',
            'AWB gesamt Ist Wochenmittelwert',
            'AWB gesamt Ist Gleitender Mittelwert',
          ],
        },
        {
          name: 'Sonstige + VV',
          color: 'rgb(217,214,87)',
          length: 5,
          fields: [
            'Sonstige Ist',
            'Sonstige Ist kumuliert',
            'Sonstige Plan kumuliert',
            'Sonstige Abweichung kumuliert',
            'Sonstige Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Sickerwasserkonzentrat',
          color: 'rgb(255,255,255)',
          length: 5,
          fields: [
            'Sickerwasserkonzentrat Ist',
            'Sickerwasserkonzentrat Ist kumuliert',
            'Sickerwasserkonzentrat Plan kumuliert',
            'Sickerwasserkonzentrat Abweichung kumuliert',
            'Sickerwasserkonzentrat Ist Wochenmittelwert',
          ],
        },
        {
          name: 'AVG-R',
          color: 'rgb(149,199,83)',
          length: 5,
          fields: ['AVGR Ist', 'AVGR Ist kumuliert', 'AVGR Plan kumuliert', 'AVGR Abweichung kumuliert', 'AVGR Ist Wochenmittelwert'],
        },
        {
          name: 'REMONDIS',
          color: 'rgb(232,53,27)',
          length: 5,
          fields: [
            'REMONDIS Ist',
            'REMONDIS Ist kumuliert',
            'REMONDIS Plan kumuliert',
            'REMONDIS Abweichung kumuliert',
            'REMONDIS Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Sortierreste, ges',
          color: 'rgb(188,158,221)',
          length: 5,
          fields: [
            'Sortierreste Ist',
            'Sortierreste Ist kumuliert',
            'Sortierreste Plan kumuliert',
            'Sortierreste Abweichung kumuliert',
            'Sortierreste Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Fe-Schrott aus VSA',
          color: 'rgb(164,160,160)',
          length: 5,
          fields: [
            'Fe-Schrott Ist',
            'Fe-Schrott Ist kumuliert',
            'Fe-Schrott Plan kumuliert',
            'Fe-Schrott Abweichung kumuliert',
            'Fe-Schrott Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Input TMB',
          color: 'rgb(255,255,201)',
          length: 5,
          fields: [
            'Input TMB Ist',
            'Input TMB Ist kumuliert',
            'Input TMB Plan kumuliert',
            'Input TMB Abweichung kumuliert',
            'Input TMB Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Input RMB',
          color: 'rgb(171,211,116)',
          length: 5,
          fields: [
            'Input RMB Ist',
            'Input RMB Ist kumuliert',
            'Input RMB Plan kumuliert',
            'Input RMB Abweichung kumuliert',
            'Input RMB Ist Wochenmittelwert',
          ],
        },
        {
          name: 'Rostasche',
          color: 'rgb(164,160,160)',
          length: 4,
          fields: ['Rostasche Ist', 'Rostasche Ist kumuliert', 'Rostasche Plan kumuliert', 'Rostasche Anteil kumuliert'],
        },
        {
          name: 'Reste Reststoffe AGR',
          color: 'rgb(239,198,166)',
          length: 4,
          fields: ['Reststoffe Ist', 'Reststoffe Ist kumuliert', 'Reststoffe Plan kumuliert', 'Reststoffe Anteil kumuliert'],
        },
        {
          name: 'Gips',
          color: 'rgb(186,208,234)',
          length: 4,
          fields: ['Gips Ist', 'Gips Ist kumuliert', 'Gips Plan kumuliert', 'Gips Anteil kumuliert'],
        },
        {
          name: 'Kalk (CaO)',
          color: 'rgb(245,187,48)',
          length: 4,
          fields: ['Kalk Ist', 'Kalk Ist kumuliert', 'Kalk Plan kumuliert', 'Kalk Bedarf kumuliert'],
        },
        {
          name: 'AW (NH4OH)',
          color: 'rgb(217,214,87)',
          length: 4,
          fields: ['Ammoniakwasser Ist', 'Ammoniakwasser Ist kumuliert', 'Ammoniakwasser Plan kumuliert', 'Ammoniakwasser Bedarf kumuliert'],
        },
        {
          name: 'Herdofenkoks HOK',
          color: 'rgb(255,255,201)',
          length: 4,
          fields: ['Herdofenkoks Ist', 'Herdofenkoks Ist kumuliert', 'Herdofenkoks Plan kumuliert', 'Herdofenkoks Bedarf kumuliert'],
        },
        {
          name: 'Stickstoff N2',
          color: 'rgb(149,199,83)',
          length: 4,
          fields: ['Stickstoff Ist', 'Stickstoff Ist kumuliert', 'Stickstoff Plan kumuliert', 'Stickstoff N2 Bedarf kumuliert'],
        },
      ],
    };
  },
  async created() {
    const yearList = await api.year.get();
    this.yearOptions = yearList.data.map(({ year }) => year);
    this.selectedYear = localStorage.getItem('selectedYear') || currentYear;
    this.header.forEach((h) => {
      if (h.fields) {
        h.fields.forEach((field) => {
          this.fields.push({
            name: field,
            color: h.color,
            obj: h.name,
          });
        });
      }
    });
    this.init();
  },
  methods: {
    scrollShift(offset) {
      document.querySelector('.table-wrapper').scrollLeft += offset;
    },
    addZeroes(num) {
      const dec = String(num).split(',');
      if (!dec[1]) {
        dec[1] = '00';
      }
      if (dec[1].length === 1) {
        dec[1] = `${dec[1]}0`;
      }
      return `${dec[0]},${dec[1]}`;
    },
    format(n) {
      return this.addZeroes(new Intl.NumberFormat('de-DE').format(Number(n).toFixed(2)));
    },
    async init() {
      localStorage.setItem('selectedYear', this.selectedYear);
      this.loaded = false;
      const responce = await api.TRAS.get({ year: this.selectedYear });
      this.data = responce.map((i) => ({
        ...i,
        KW: i['Kalender-woche'],
        'Anl. Tag': i['Anliefertag des Jahres'],
        Date: moment(i.date).format('DD-MM-YYYY'),
      }));

      const metrics = await api.metrics.get({ year: this.selectedYear });
      this.metrics = metrics;

      this.loaded = true;
    },
    exportCSV() {
      const table = this.$refs.tableData;

      const rows = table.getElementsByTagName('tr');
      const csvData = [...rows].map((row) => {
        const cols = row.querySelectorAll('td,th');
        const csvRow = [...cols].map((col) => {
          let data = col.innerText;
          data = data.split('\n').join(' ');
          if (data.includes(',')) {
            data = `"${data}"`;
          }
          if (col.colSpan > 1) {
            const colSpanArr = [...Array(col.colSpan - 1).keys()].map(() => ';');
            data = [data, ...colSpanArr].join('');
          }
          return data;
        });
        return csvRow.join(';');
      });
      const csvFinalData = csvData.join('\n');

      const csvFile = new Blob(['\uFEFF' + csvFinalData], { type: 'text/csv; charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(csvFile);
      link.download = 'TRAS_values';
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped lang="scss">
.planned-content {
  overflow-x: hidden;
}

.line-value {
  display: flex;
  margin-left: 20px;
  align-items: center;
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  margin-top: 20px;
  padding: 10px;
}

.type-value {
  margin-left: 10px;
  width: 90px;
}

table,
td,
th {
  box-shadow: inset 0 0 0 1px #333;
  background: white;
}
th {
  padding: 5px;
}
td {
  padding: 5px;
  text-align: right;
}
tr th:first-child,
tr td:first-child {
  position: sticky;
  left: 0;
  width: 45px;
  min-width: 45px;
  max-width: 45px;
}
tr th:nth-child(2),
tr td:nth-child(2) {
  position: sticky;
  left: 45px;
  width: 60px;
  min-width: 60px;
  max-width: 60px;
}
tr th:nth-child(3),
tr td:nth-child(3) {
  position: sticky;
  left: 105px;
}
tr th:first-child,
tr th:nth-child(2),
tr th:nth-child(3) {
  z-index: 1;
}
thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.no-data {
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-wrap-header {
  white-space: nowrap;
}

.center-header {
  text-align: center;
}

.metrics-header {
  padding: 2px;
}
.metrics-number {
  background: rgb(67, 163, 233);
}
.field-name-header {
  height: 405px;
  position: relative;
  min-width: 50px;
}

.rotate-header {
  -ms-writing-mode: tb-rl;
  -webkit-writing-mode: vertical-rl;
  -moz-writing-mode: vertical-rl;
  -ms-writing-mode: vertical-rl;
  writing-mode: vertical-rl;
  transform: rotate(-180deg);
  white-space: nowrap;
  height: 405px;
  position: absolute;
  top: -5px;
  display: flex;
  align-items: center;
  width: 100%;
  right: 1px;
}

.arrows-block {
  margin-left: 50px;
  display: flex;
  width: 90px;
  justify-content: space-between;
}

.export-btn {
  display: block;
}

.actions-wrapper {
  flex-direction: row;
  justify-content: flex-end;
  display: flex;
  align-items: flex-start;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 30px;
}
</style>
